import React from "react";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE, useAppSelector } from "@hypexone/uicore";
import { domain } from "../../../constants/Branding";
import { getEnvVariable } from "../../../constants/brandConfig";
import LoginPageBanner from "../../../assets/login_page_banner.webp";
import SignUpForm from "./SignUpForm";
import downloadAndroidApp from "../../../assets/images/download-android-app.png";
import { FaArrowLeftLong } from "react-icons/fa6";
import {
  PolicyLinks,
  ContactCards,
} from "../../components/shared/FooterContactLinks";

interface SignUpModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const SignUpModal: React.FC<SignUpModalProps> = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { domainConfig } = useAppSelector((state) => state.common);
  const { langData } = useAppSelector((state) => state.cms);
  const apkUrl = getEnvVariable(domain, "apk_url") || domainConfig?.apkUrl;

  const downloadApp = () => {
    if (!apkUrl) return;
    const link = document.createElement("a");
    link.href = apkUrl.startsWith("http") ? apkUrl : `https://${apkUrl}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBack = () => {
    onClose();
    navigate(LOGIN_ROUTE);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto bg-black/70 p-0 sm:p-5"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative my-0 flex h-full max-h-none w-full max-w-none flex-col overflow-hidden rounded-none border-0 bg-goldenYellow shadow-[0_16px_48px_rgba(0,0,0,0.45)] sm:my-auto sm:h-auto sm:max-h-[min(92dvh,920px)] sm:max-w-md sm:rounded-2xl sm:border sm:border-black/20 md:max-w-4xl md:flex-row md:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signup-modal-title"
      >
        <div
          className="relative flex h-[230px] w-full shrink-0 items-center justify-center bg-cover bg-center bg-no-repeat [clip-path:ellipse(120%_100%_at_50%_0%)] md:h-auto md:w-[38%] md:min-w-[280px] md:self-stretch md:border-r md:border-black/30 md:[clip-path:none]"
          style={{ backgroundImage: `url(${LoginPageBanner})` }}
        >
          <div className="absolute inset-0 bg-black/25" />
          <button
            type="button"
            onClick={handleBack}
            className="absolute left-2.5 top-2.5 z-20 flex items-center gap-1.5 rounded-full bg-black/95 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg transition-colors hover:bg-black md:hidden"
            aria-label="Back to sign in"
          >
            <FaArrowLeftLong className="h-3.5 w-3.5" />
            <span>{langData?.["back"] ?? "Back"}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-2.5 top-2.5 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-black/95 text-white shadow-lg hover:opacity-90 md:hidden"
            aria-label="Close"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={getEnvVariable(domain, "logo")}
            alt="Logo"
            className="relative z-10 max-h-[70px] w-auto object-contain md:max-h-[110px]"
          />
        </div>

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <button
            type="button"
            onClick={handleBack}
            className="absolute left-3 top-3 z-10 hidden items-center gap-1.5 rounded-full bg-black/95 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg transition-colors hover:bg-black md:flex"
            aria-label="Back to sign in"
          >
            <FaArrowLeftLong className="h-3.5 w-3.5" />
            <span>{langData?.["back"] ?? "Back"}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 hidden h-8 w-8 items-center justify-center rounded-full bg-black/95 text-white shadow-lg hover:opacity-90 md:flex"
            aria-label="Close"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 md:px-8 md:py-6 md:pt-12">
            <div className="mx-auto flex w-full max-w-md justify-center">
              <SignUpForm onSuccess={onSuccess} />
            </div>

            <div className="mx-auto mt-[2.5rem] flex w-full max-w-md flex-col gap-[2.5rem]">
              <PolicyLinks />
              <ContactCards
                cardClassName="border border-black/10 bg-[#ffe9b3]"
                emailSlot={
                  apkUrl ? (
                    <button
                      type="button"
                      onClick={downloadApp}
                      className="flex h-full w-full cursor-pointer items-center justify-center transition-all hover:brightness-110"
                      aria-label={
                        langData?.["download_apk"] ?? "Download Android APP"
                      }
                    >
                      <img
                        src={downloadAndroidApp}
                        alt=""
                        className="h-full max-h-full w-full object-contain"
                      />
                    </button>
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpModal;
